import React from "react"
import classnames from "classnames"

function capitalizeFirstLetter(str) {
  if (typeof str !== "string") {
    return null
  }
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function DesktopChart({
  bottomQuartile,
  comparisonName,
  name,
  comparison,
  max,
  min,
  median,
  screens,
  topQuartile,
  benchmarkType,
  primaryFactorNarrative,
}) {
  const chartHeight = 260
  const chartSteps = 9
  const minToMedian = median - min
  const maxToMedian = max - median
  const largestDifferenceToMedian = Math.max(minToMedian, maxToMedian)
  const percentagePerPx = chartHeight / 2 / largestDifferenceToMedian
  const comparisonBottomOffsetPx = (comparison - median) * percentagePerPx
  return (
    <div
      className={classnames("cf:flex-1 cf:flex", {
        "desktop:cf:mb-28 wide:cf:mb-28 cf:mb-16":
          primaryFactorNarrative !== undefined,
        "desktop:cf:mb-56 wide:cf:mb-56 cf:mb-32":
          primaryFactorNarrative === undefined,
      })}
    >
      <div className="cf:w-1/12 mobile:cf:hidden tablet:cf:hidden"></div>
      <div
        className="cf:relative desktop:cf:w-10/12 wide:cf:w-10/12 cf:w-full"
        style={{ height: "400px" }}
      >
        <div
          className={classnames("cf:relative cf:w-full", {
            "cf:mt-24": largestDifferenceToMedian - maxToMedian < 40,
            "cf:mb-48": largestDifferenceToMedian - minToMedian < 40,
          })}
          style={{ height: chartHeight }}
        >
          <>
            {/* Background grid lines */}
            {[...Array(chartSteps)].map((_, index) => {
              return (
                <div
                  key={index}
                  className={classnames(
                    "cf:absolute cf:w-full cf:-mt-1 border-black-30 cf:border",
                    {
                      // Hide the median
                      "cf:hidden": index === Math.ceil(chartSteps / 2) - 1,
                    },
                  )}
                  style={{
                    top: `${(index / (chartSteps - 1)) * 100}%`,
                  }}
                />
              )
            })}

            {/* Comparison line */}
            <div
              className="cf:absolute cf:w-full cf:flex"
              style={{ top: "50%" }}
            >
              {/* Render as one or lines depending on separation */}
              {comparisonName ? (
                Math.abs(comparisonBottomOffsetPx) > 20 ? (
                  <div
                    className="cf:absolute cf:w-full cf:border-b-2 border-black-30 cf:border-dashed"
                    style={{
                      bottom: `${comparisonBottomOffsetPx}px`,
                      marginTop: -1,
                    }}
                  >
                    <span
                      className="cf:absolute cf:leading-tight cf:text-right cf:mr-16 cf:transform cf:-translate-y-1/2 cf:text-sm tablet:cf:hidden mobile:cf:hidden"
                      style={{ right: "100%", width: "130px" }}
                    >
                      {comparisonName
                        ? capitalizeFirstLetter(comparisonName)
                        : name}{" "}
                      median is {comparison ? comparison : median}%
                    </span>
                  </div>
                ) : (
                  <>
                    <div
                      className="cf:absolute cf:w-2/5 cf:border-b-2 border-black-30 cf:border-dashed cf:left-0"
                      style={{
                        bottom: `${comparisonBottomOffsetPx}px`,
                        marginTop: -1,
                      }}
                    >
                      <span
                        className="cf:absolute cf:leading-none cf:text-right cf:mr-16 cf:transform cf:-translate-y-1/2 cf:text-sm tablet:cf:hidden mobile:cf:hidden"
                        style={{ right: "100%", width: "130px" }}
                      >
                        {comparisonName
                          ? capitalizeFirstLetter(comparisonName)
                          : name}{" "}
                        median is {comparison ? comparison : median}%
                      </span>
                    </div>
                    <div
                      className="cf:absolute cf:w-2/5 cf:border-b-2 border-black-30 cf:border-dashed cf:right-0"
                      style={{
                        bottom: `${comparisonBottomOffsetPx}px`,
                        marginTop: -1,
                      }}
                    ></div>
                  </>
                )
              ) : null}
              {/* min */}
              <div className="cf:w-1/5 cf:relative">
                <div
                  className="cf:absolute bg-purple-300 cf:transform cf:-translate-x-1/2"
                  style={{
                    left: "50%",
                    height: `${(median - min) * percentagePerPx}px`,
                    maxWidth: "120px",
                    top: "100%",
                    width: "60%",
                  }}
                >
                  <div
                    className="text-purple-500 cf:font-display cf:tracking-tight cf:text-3xl mobile:cf:text-base tablet:cf:text-2xl cf:font-bold cf:text-center cf:mt-4 cf:absolute cf:w-full"
                    style={{ top: "100%" }}
                  >
                    {min}%
                  </div>
                </div>
              </div>
              {/* Bottom 25 */}
              <div className="cf:w-1/5 cf:relative">
                <div
                  className="cf:absolute bg-purple-300 cf:transform cf:-translate-x-1/2"
                  style={{
                    left: "50%",
                    height: `${(median - bottomQuartile) * percentagePerPx}px`,
                    maxWidth: "120px",
                    top: "100%",
                    width: "60%",
                  }}
                >
                  <div
                    className="text-purple-500 cf:font-display cf:tracking-tight cf:text-3xl mobile:cf:text-base tablet:cf:text-2xl cf:font-bold cf:text-center cf:mt-4 cf:absolute cf:w-full"
                    style={{ top: "100%" }}
                  >
                    {bottomQuartile}%
                  </div>
                </div>
              </div>
              {/* Median */}
              <div className="cf:w-1/5 cf:relative">
                <span className="cf:absolute cf:w-full text-blue-500 cf:font-display cf:tracking-tight cf:leading-none cf:text-3xl mobile:cf:text-base tablet:cf:text-2xl cf:font-bold cf:text-center cf:transform cf:-translate-y-1/2">
                  {median}% {benchmarkType == "covid-19" ? "confident" : null}
                  {benchmarkType == "engagement" ? "engaged" : null}
                  {benchmarkType == "return-to-workplace" ? "ready" : null}
                </span>
              </div>
              {/* Top 25 */}
              <div className="cf:w-1/5 cf:relative">
                <div
                  className="cf:absolute bg-blue-300 cf:transform cf:-translate-x-1/2"
                  style={{
                    left: "50%",
                    height: `${(topQuartile - median) * percentagePerPx}px`,
                    maxWidth: "120px",
                    bottom: "100%",
                    width: "60%",
                  }}
                >
                  <div
                    className="text-blue-500 cf:font-display cf:tracking-tight cf:text-3xl mobile:cf:text-base tablet:cf:text-2xl cf:font-bold cf:text-center cf:mb-4 cf:absolute cf:w-full"
                    style={{ bottom: "100%" }}
                  >
                    {topQuartile}%
                  </div>
                </div>
              </div>
              {/* max */}
              <div className="cf:w-1/5 cf:relative">
                <div
                  className="cf:absolute bg-blue-300 cf:transform cf:-translate-x-1/2"
                  style={{
                    left: "50%",
                    height: `${(max - median) * percentagePerPx}px`,
                    maxWidth: "120px",
                    bottom: "100%",
                    width: "60%",
                  }}
                >
                  <div
                    className="text-blue-500 cf:font-display cf:tracking-tight cf:text-3xl mobile:cf:text-base tablet:cf:text-2xl cf:font-bold cf:text-center cf:mb-4 cf:absolute cf:w-full"
                    style={{ bottom: "100%" }}
                  >
                    {max}%
                  </div>
                </div>
              </div>
              {/* Median lines */}
              <div className="border-black-30 cf:absolute cf:w-2/5 cf:left-0 cf:border cf:top-0"></div>
              <div className="border-black-30 cf:absolute cf:w-2/5 cf:right-0 cf:border cf:top-0"></div>
            </div>
          </>
        </div>
        <div className="cf:flex tablet:cf:text-sm mobile:cf:text-xs">
          <p className="cf:flex-1 cf:text-center">Bottom 10%</p>
          <p className="cf:flex-1 cf:text-center">Bottom 25%</p>
          <div className="cf:flex-1 cf:text-center">
            <p>Median</p>
            <p>
              {name ? (
                name
              ) : (
                <div>
                  Median{" "}
                  <span className="mobile:cf:hidden tablet:cf:hidden">
                    {" "}
                    engagement score
                  </span>
                </div>
              )}
            </p>
          </div>
          <p className="cf:flex-1 cf:text-center">Top 25%</p>
          <p className="cf:flex-1 cf:text-center">Top 10%</p>
        </div>
        {/* Comparison legend for smaller s */}
        {(screens.mobileOnly || screens.tabletOnly) && (
          <div className="cf:text-sm cf:mt-24 cf:flex cf:items-center">
            <span
              className="cf:border-b-2 border-black-30 cf:border-dashed cf:mr-12"
              style={{ width: 24 }}
            ></span>
            <p>
              {comparisonName} median ({comparison}%)
            </p>
          </div>
        )}
      </div>
      {!screens.mobileOnly && benchmarkType != "covid-19" && (
        <div
          className={classnames(
            "cf:relative desktop:cf:w-1/12 wide:cf:w-1/12 tablet:cf:w-1/5 mobile:cf:hidden",
            {
              "cf:mt-24": largestDifferenceToMedian - maxToMedian < 40,
              "cf:mb-48": largestDifferenceToMedian - minToMedian < 40,
            },
          )}
          style={{ height: chartHeight }}
        >
          <MiniReference
            bottomQuartile={bottomQuartile}
            max={max}
            min={min}
            median={median}
            topQuartile={topQuartile}
          />
        </div>
      )}
    </div>
  )
}

function MiniReference({ bottomQuartile, max, min, median, topQuartile }) {
  const height = 240
  const numberHeight = 24
  const chartHeight = height - numberHeight * 2 - 4
  return (
    <div
      className="cf:absolute cf:flex cf:flex-col cf:transform cf:-translate-y-1/2 cf:-translate-x-1/2"
      style={{
        top: "50%",
        left: "50%",
        marginTop: 1 + chartHeight * ((median - 50) / 100),
        height,
      }}
    >
      <p
        className="cf:font-display cf:text-sm cf:leading-tight cf:text-center cf:font-bold cf:mb-8"
        style={{ height: 16 }}
      >
        100
      </p>
      <div
        className="cf:relative cf:border-2 border-black-30 cf:rounded-lg cf:flex-1"
        style={{ width: 24 }}
      >
        {/* Background blends made with two elements covering the min/max range */}
        {/* <div
          className="cf:absolute cf:w-full"
          style={{
            opacity: 0.4,
            top: `${100 - max}%`,
            height: `${max - min}%`,
          }}
        ></div>
        <div
          className="cf:absolute cf:w-full"
          style={{
            opacity: 0.4,
            top: `${100 - topQuartile}%`,
            height: `${topQuartile - bottomQuartile}%`,
          }}
        ></div> */}
        {/* Min line */}
        <div
          className="cf:border border-purple-300 cf:absolute cf:w-full"
          style={{
            top: `${100 - min}%`,
            marginTop: -1,
          }}
        >
          <span
            className="cf:absolute cf:font-display cf:font-bold cf:text-sm cf:leading-tight cf:mr-12"
            style={{ right: "100%", marginTop: -8 }}
          >
            {min}
          </span>
        </div>
        {/* Bottom quartile line */}
        <div
          className="cf:border border-purple-300 cf:absolute cf:w-full"
          style={{
            top: `${100 - bottomQuartile}%`,
            marginTop: -1,
          }}
        >
          <span
            className="cf:absolute cf:font-display cf:font-bold cf:text-sm cf:leading-tight cf:ml-12"
            style={{ left: "100%", marginTop: -8 }}
          >
            {bottomQuartile}
          </span>
        </div>
        {/* Median line */}
        <div
          className="cf:border border-black-30 cf:absolute cf:w-full"
          style={{
            top: `${100 - median}%`,
            marginTop: -1,
          }}
        >
          <span
            className="cf:absolute cf:font-display cf:font-bold cf:text-sm cf:leading-tight cf:mr-12"
            style={{ right: "100%", marginTop: -8 }}
          >
            {median}
          </span>
        </div>
        {/* Top quartile line */}
        <div
          className="cf:border border-blue-300 cf:absolute cf:w-full"
          style={{
            top: `${100 - topQuartile}%`,
            marginTop: -1,
          }}
        >
          <span
            className="cf:absolute cf:font-display cf:font-bold cf:text-sm cf:leading-tight cf:ml-12"
            style={{ left: "100%", marginTop: -8 }}
          >
            {topQuartile}
          </span>
        </div>
        {/* Max line */}
        <div
          className="cf:border border-blue-300 cf:absolute cf:w-full"
          style={{
            top: `${100 - max}%`,
            marginTop: -1,
          }}
        >
          <span
            className="cf:absolute cf:font-display cf:font-bold cf:text-sm cf:leading-tight cf:mr-12"
            style={{ right: "100%", marginTop: -8 }}
          >
            {max}
          </span>
        </div>
      </div>
      <p
        className="cf:font-display cf:text-sm cf:leading-tight cf:text-center cf:font-bold cf:mt-8"
        style={{ height: 16 }}
      >
        0
      </p>
    </div>
  )
}
